import { useContext } from 'react';
import { CSSTransition } from 'react-transition-group';

import Text from 'components/common/Text';
import { PickYourMealsContext, UserContext } from 'components/signUpFunnel/Contexts';
import IdMeToggle from 'components/signUpFunnel/idMe/IdMeToggle';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import { STANDARD, TEMPO } from 'lib/constants';
import {
  MILITARY_MEDICAL_TEACHER_SCOPES,
  STUDENT_ALUMNI_GOV_SCOPES,
  endpointFor,
} from 'lib/constants/idme';

const IdMeForm = ({
  showIdMeError,
  transitionRef,
  brand,
  isFamilyMealPlan,
  isBilling = false,
  voucher,
  hasIdMe,
}) => {
  const { isPickYourMeals } = useContext(PickYourMealsContext);
  const mealPlan = useFunnelMealPlan();
  const { user } = useContext(UserContext);
  const { data: partnershipData } = usePartnershipQuery(user?.partnerReferrer);

  const getOfferText = scopes => {
    const partnershipPromotionalText =
      voucher?.partnershipPromotionalText || partnershipData?.partnership?.promotionalText;

    if (scopes === MILITARY_MEDICAL_TEACHER_SCOPES) {
      const basePromoText = 'Save 50% on your first box (up to $60) and 10% on future orders.';
      let additionalBenefits;

      if (mealPlan === STANDARD) {
        additionalBenefits =
          ' Plus get free shipping on the first box and a free dessert item for life.';
      } else {
        additionalBenefits = '';
      }

      const promoText = `${basePromoText}${additionalBenefits}`;
      return partnershipPromotionalText ? `${promoText} ${partnershipPromotionalText}` : promoText;
    }

    if (scopes === STUDENT_ALUMNI_GOV_SCOPES) {
      const promoText =
        brand === TEMPO
          ? 'Get 50% off your first box and 20% off your next month.'
          : 'Get 50% off your first 4 boxes, up to $47.50 per box.';

      return partnershipPromotionalText ? `${promoText} ${partnershipPromotionalText}` : promoText;
    }
  };

  return (
    <>
      <IdMeToggle
        endpoint={endpointFor({
          brand: brand,
          scopes: MILITARY_MEDICAL_TEACHER_SCOPES,
          billing: isBilling,
          family: isFamilyMealPlan,
          isPickYourMeals: isPickYourMeals,
        })}
        hasIdMe={
          (hasIdMe || voucher?.isIdMeMilitaryHealthTeacher) && !voucher?.isIdMeStudentAlumniGov
        }
        hasAdditionalPromo={
          !voucher?.isIdMeMilitaryHealthTeacher && !voucher?.isIdMeStudentAlumniGov
        }
        headerText="Military, First Responders, Health Care Professionals and Teachers"
        recurringDiscountText="An ID.me 10% discount (up to $60) will be applied to future orders after the promotion above has been redeemed."
        offerText={getOfferText(MILITARY_MEDICAL_TEACHER_SCOPES)}
        isBilling={isBilling}
      />
      <IdMeToggle
        endpoint={endpointFor({
          brand: brand,
          scopes: STUDENT_ALUMNI_GOV_SCOPES,
          billing: isBilling,
          family: isFamilyMealPlan,
          isPickYourMeals: isPickYourMeals,
        })}
        hasIdMe={voucher?.isIdMeStudentAlumniGov}
        headerText="Students, Alumni, and Government Officials"
        offerText={getOfferText(STUDENT_ALUMNI_GOV_SCOPES)}
        isBilling={isBilling}
      />
      {showIdMeError && (
        <CSSTransition
          in={Boolean(showIdMeError)}
          timeout={100}
          classNames="fadeInOut"
          unmountOnExit
          nodeRef={transitionRef}
        >
          <div ref={transitionRef}>
            <Text brandStylesOverrides="text-negative" className="top-14 mt-1 w-full bp1:px-10">
              An error occurred while verifying your information. Please contact our support.
            </Text>
          </div>
        </CSSTransition>
      )}
    </>
  );
};

IdMeForm.propTypes = {
  voucher: PropTypes.object,
  showIdMeError: PropTypes.bool,
  transitionRef: PropTypes.object,
  brand: PropTypes.string,
  isFamilyMealPlan: PropTypes.bool,
  isBilling: PropTypes.bool,
  hasIdMe: PropTypes.bool,
};

export default IdMeForm;
